import { useEffect, useMemo, useState } from 'react';

import {
  getRecordsRequestAgentDisplayState,
  RECORDS_REQUEST_AGENT_TERMINAL_STATUSES,
} from '@/components/agentflow/recordsRequestAgentState';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useGetRecordsRequestStatusQuery } from '@/services/api/agentflowService';
import type { Task } from '@/services/types/client-intake-types';

const BADGE_STYLES = {
  idle: 'border-zinc-200 bg-zinc-50 text-zinc-500',
  running: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  ready: 'border-green-200 bg-green-50 text-green-700',
  blocked: 'border-amber-200 bg-amber-50 text-amber-700',
  failed: 'border-red-200 bg-red-50 text-red-700',
} as const;

const DOT_STYLES = {
  idle: 'bg-zinc-400',
  running: 'bg-indigo-500 animate-pulse',
  ready: 'bg-green-500',
  blocked: 'bg-amber-500',
  failed: 'bg-red-500',
} as const;

function getBadgeLabel(status: string | null, blockerCount: number): string {
  if (status === 'running') return 'Working';
  if (status === 'ready') return 'Ready';
  if (status === 'blocked') {
    if (blockerCount === 1) return '1 input';
    if (blockerCount > 1) return `${blockerCount} inputs`;
    return 'Needs input';
  }
  if (status === 'failed') return 'Failed';
  return 'Idle';
}

function getTooltipText(status: string | null, blockerCount: number): string {
  if (status === 'running') return 'Submission attempt is in progress';
  if (status === 'ready') return 'Submission attempt is ready to review';
  if (status === 'blocked') {
    if (blockerCount === 1) return 'Submission attempt has 1 unresolved item';
    if (blockerCount > 1) {
      return `Submission attempt has ${blockerCount} unresolved items`;
    }
    return 'Submission attempt needs input';
  }
  if (status === 'failed') return 'Submission attempt failed';
  return 'Submission attempt has not started';
}

export const RecordsRequestAgentStatusCell = ({ task }: { task: Task }) => {
  const matterId = task.matter;
  const requestId = task.treatment_record_request?.id;
  const [hasTerminalStatus, setHasTerminalStatus] = useState(false);

  const { data } = useGetRecordsRequestStatusQuery(
    { matterId, requestId: requestId ?? '' },
    {
      skip: !matterId || !requestId,
      pollingInterval: hasTerminalStatus ? undefined : 10000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  useEffect(() => {
    if (!data) return;
    setHasTerminalStatus(
      RECORDS_REQUEST_AGENT_TERMINAL_STATUSES.has(data.status),
    );
  }, [data]);

  const displayState = useMemo(() => {
    if (!data || data.status === 'not_found') return null;
    return getRecordsRequestAgentDisplayState(data);
  }, [data]);

  const blockerCount = data?.blocker_count ?? data?.placeholder_count ?? 0;
  const badgeState = displayState ?? 'idle';
  const label = getBadgeLabel(displayState, blockerCount);
  const tooltipText = getTooltipText(displayState, blockerCount);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
            BADGE_STYLES[badgeState],
          )}
        >
          <span
            className={cn(
              'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
              DOT_STYLES[badgeState],
            )}
            aria-hidden="true"
          />
          <span className="truncate">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="text-xs">
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
};
